import { FormChangeEvent } from '../../hooks/useFormValidation';
import { gaEvent } from '@domgen/shared/services/ga/ga';
import { SystemCode } from '../../models/systemCode';
import { checkFormEventFieldName } from '../../utils/form/checkFormEventFieldName';

/**
 * Handles Google Analytics events for the personal details form.
 * This function tracks user interactions with specific fields in the form
 * and sends corresponding Google Analytics events based on the field name and other parameters.
 *
 * NOTE: Checkbox events are handled in the form component where the field is instantiated. This is due to the
 * event value not being a string value and should fire on a click event not an onBlur event like the input fields
 * captured here.
 *
 * @param formEvent - The form event or field name as a string. This is used to determine
 *                    which field triggered the event.
 * @param pagename - The name of the page where the form resides. Must be either
 *                   'appliance-location' or 'personal-details'.
 * @param applianceCategory - The super category of the appliance. This is used to categorize
 *
 * @param applianceName - The specific name of the appliance.
 *
 * @param sessionId - The session ID for the current user session. This is included in the
 *                    GA event for tracking purposes.
 * @param systemCode - (Optional) The system code for the registration. Defaults to 'DandG_IPR'
 *                     if not provided.
 * @param addressEventAction - (Optional) Specifies whether the address was entered manually
 *                             or automatically. Defaults to 'Manual'.
 */
export function handleGaPersonalDetailsFormEvents(
  formEvent: FormChangeEvent | string,
  pagename: 'olr-appliance-location' | 'olr-personal-details',
  applianceCategory: string,
  applianceName: string,
  sessionId: string,
  systemCode?: SystemCode,
  addressEventAction: 'Manual' | 'Auto' = 'Manual',
) {
  const event = {
    event: 'genericGAEvent',
    eventCategory: 'appliance details form USA',
    eventAction: `${addressEventAction} Address`,
    type: 'STANDARD',
    category: 'registration-pages',
    applianceCategory,
    applianceName,
    appSessionID: sessionId,
    regSystemCode: systemCode ?? 'DandG_IPR',
    pagename,
  };

  const fieldName = checkFormEventFieldName(formEvent);

  switch (fieldName) {
    case 'addressLine1':
    case 'merchandiseLocationLine1':
      gaEvent({
        ...event,
        eventLabel: 'COMPLETE - Address - line 1',
      });
      break;
    case 'addressLine2':
    case 'merchandiseLocationLine2':
      gaEvent({
        ...event,
        eventLabel: 'COMPLETE - Address - line 2',
      });
      break;
    case 'regionOrStateCode':
    case 'merchandiseLocationRegionOrStateCode':
      gaEvent({
        ...event,
        eventLabel: 'COMPLETE - State',
      });
      break;
    case 'city':
    case 'merchandiseLocationCity':
      gaEvent({
        ...event,
        eventLabel: 'COMPLETE - City',
      });
      break;
    case 'postalCode':
    case 'merchandiseLocationPostalCode':
      gaEvent({
        ...event,
        eventLabel: 'COMPLETE - Postal Code',
      });
      break;

    //Handle personal details non-address fields
    case 'firstName':
      gaEvent({
        ...event,
        eventLabel: 'COMPLETE',
        eventAction: 'firstName',
      });
      break;
    case 'lastName':
      gaEvent({
        ...event,
        eventLabel: 'COMPLETE',
        eventAction: 'lastName',
      });
      break;
    case 'email':
      gaEvent({
        ...event,
        eventLabel: 'COMPLETE',
        eventAction: 'emailAddress',
      });
      break;
    case 'phoneNumber':
      gaEvent({
        ...event,
        eventLabel: 'COMPLETE',
        eventAction: 'telephone',
      });
      break;

    default:
      return;
  }
}
